export const MIN_PART_BPM = 20;
export const MAX_PART_BPM = 300;

const toBpm = (value) => {
  const bpm = Number(value);

  return Number.isFinite(bpm) && bpm >= MIN_PART_BPM && bpm <= MAX_PART_BPM ? bpm : null;
};

/**
 * The tempo a part plays at when it isn't the song's, or null.
 *
 * Unlike a part key it covers that part alone — the part after it is back at the song's BPM
 * without having to say so. A duplicate never sets one.
 */
export function getPartBpm(part, songBpm) {
  if (!part || part.isDuplicate) return null;

  const bpm = toBpm(part.bpm);

  return bpm && bpm !== Number(songBpm) ? bpm : null;
}

/**
 * The tempo markers for the parts being rendered, keyed by part id.
 *
 * `enter` is the BPM moved from, on the part a tempo starts; `exit` is the BPM moved to, on the part
 * it ends. Back-to-back parts at the same BPM read as one run, so the page doesn't go back to the
 * song's tempo just to leave it again.
 *
 * @param {Array<object>} parts - The parts being rendered, in order
 * @param {number|string} songBpm - The song's BPM
 * @returns {Object<string, { bpm: number, enter?: number, exit?: number }>}
 */
export function getPartTempos(parts = [], songBpm) {
  const song = toBpm(songBpm);
  const tempos = {};
  if (!song) return tempos;

  const bpms = parts.map((part) => getPartBpm(part, song));

  parts.forEach((part, i) => {
    const bpm = bpms[i];
    if (!bpm) return;

    const before = bpms[i - 1] || song;
    const after = bpms[i + 1] || song;

    tempos[part.id] = {
      bpm,
      ...(before !== bpm && { enter: before }),
      ...(after !== bpm && { exit: after }),
    };
  });

  return tempos;
}

// A pause longer than this starts a new count
export const TAP_RESET_MS = 2000;
const TAPS_KEPT = 8;

/**
 * The taps to keep after one at `now`: the recent ones, or just this one after a pause.
 */
export function addTap(taps = [], now) {
  const last = taps[taps.length - 1];
  if (last === undefined || now - last > TAP_RESET_MS) return [now];

  return [...taps, now].slice(-TAPS_KEPT);
}

/**
 * The BPM a run of taps is keeping, averaged over their intervals, or null before a second tap.
 */
export function bpmFromTaps(taps = []) {
  if (taps.length < 2) return null;

  const averageMs = (taps[taps.length - 1] - taps[0]) / (taps.length - 1);
  if (!(averageMs > 0)) return null;

  return Math.min(MAX_PART_BPM, Math.max(MIN_PART_BPM, Math.round(60000 / averageMs)));
}
